import React, { useState, useEffect } from 'react';
import { useClient } from '../../context/ClientContext';

const PERF_BADGE = {
  BEST: { label: 'Best', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  GOOD: { label: 'Good', bg: 'bg-blue-100', text: 'text-blue-700' },
  LEARNING: { label: 'Learning', bg: 'bg-amber-100', text: 'text-amber-700' },
  LOW: { label: 'Low', bg: 'bg-red-100', text: 'text-red-700' },
  UNRATED: { label: 'Unrated', bg: 'bg-gray-100', text: 'text-gray-500' }
};

export default function AdCopyWinners({ dateRange }) {
  const { brand, fetcher } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('HEADLINE');

  useEffect(() => {
    setLoading(true);
    fetcher.fetchAdAssetPerformance(dateRange)
      .then(setData)
      .finally(() => setLoading(false));
  }, [dateRange]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-gray-200 rounded"></div>)}
        </div>
      </div>
    );
  }

  if (!data?.assets?.length) return null;

  const headlines = data.assets.filter(a => a.fieldType === 'HEADLINE');
  const descriptions = data.assets.filter(a => a.fieldType === 'DESCRIPTION');
  const activeAssets = activeTab === 'HEADLINE' ? headlines : descriptions;

  if (headlines.length === 0 && descriptions.length === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1"
          style={{ fontFamily: brand.typography?.headerFont }}>
        Ad Copy Performance
      </h3>
      <p className="text-xs text-gray-400 mb-4">How Google rates your ad assets</p>

      <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5 mb-4 w-fit">
        <button
          onClick={() => setActiveTab('HEADLINE')}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeTab === 'HEADLINE' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Headlines ({headlines.length})
        </button>
        <button
          onClick={() => setActiveTab('DESCRIPTION')}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeTab === 'DESCRIPTION' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Descriptions ({descriptions.length})
        </button>
      </div>

      <div className="space-y-2">
        {activeAssets.slice(0, 15).map((asset, idx) => {
          const badge = PERF_BADGE[asset.performance] || PERF_BADGE.UNRATED;
          return (
            <div key={idx} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 truncate">{asset.text}</p>
              </div>
              <span className={`${badge.bg} ${badge.text} text-[10px] font-medium px-2 py-0.5 rounded flex-shrink-0`}>
                {badge.label}
              </span>
              <div className="flex items-center gap-3 flex-shrink-0 text-right text-xs text-gray-500">
                <span>{asset.impressions.toLocaleString()} imp.</span>
                <span>{asset.clicks} clicks</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
